import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageTransition from "@/components/layout/PageTransition";
import ShowcaseLanding from "@/components/ShowcaseLanding";
import OAuthConsent from "@/pages/OAuthConsent";
import Footer from "@/components/home/Footer";
import * as P from "./lazyPages";

// Routes that should NOT render the global footer (full-screen flows, dashboards, admin)
const FOOTER_EXCLUDED_PREFIXES = [
  "/dashboard",
  "/admin",
  "/auth",
  "/reset-password",
  "/loyalty/checkin",
  "/swiper",
  "/dev/",
  "/.lovable/oauth",
];

function shouldShowFooter(pathname: string) {
  return !FOOTER_EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname === p);
}


export default function AppRoutes() {
  const location = useLocation();
  const showFooter = shouldShowFooter(location.pathname);
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <PageTransition key={location.pathname}>

        <Routes location={location}>
          {/* Public Routes */}
          <Route path="/" element={<ShowcaseLanding />} />
          <Route path="/home" element={<P.Index />} />
          <Route path="/search" element={<P.Search />} />
          <Route path="/restaurants" element={<P.Restaurants />} />
          <Route path="/restaurant/:id" element={<P.RestaurantDetail />} />
          <Route path="/restaurant/:id/menu" element={<P.RestaurantMenu />} />
          <Route path="/auth" element={<P.Auth />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/reset-password" element={<P.ResetPassword />} />
          <Route path="/deal/:id" element={<P.DealDetail />} />
          <Route path="/wallet" element={<Navigate to="/bookings" replace />} />
          <Route path="/messages" element={<P.CustomerMessages />} />
          <Route path="/bookings" element={<P.CustomerBookings />} />
          <Route path="/savings" element={<P.CustomerSavings />} />
          <Route path="/settings" element={<P.CustomerSettings />} />
          <Route path="/reviews" element={<P.CustomerReviews />} />
          <Route path="/loyalty" element={<P.LoyaltyWallet />} />
          <Route path="/invite" element={<P.Invite />} />
          <Route path="/loyalty/checkin" element={<P.LoyaltyCheckin />} />
          <Route path="/loyalty/info" element={<P.LoyaltyInfo />} />
          <Route path="/so-funktionierts" element={<P.HowItWorksPage />} />
          <Route path="/ueber-uns" element={<P.About />} />
          <Route path="/fuer-gastronomen" element={<P.ForRestaurants />} />
          <Route path="/prepaid-deals" element={<P.PrepaidDeals />} />
          <Route path="/kontakt" element={<P.Contact />} />
          <Route path="/impressum" element={<P.Impressum />} />
          <Route path="/datenschutz" element={<P.Privacy />} />
          <Route path="/agb" element={<P.Terms />} />
          <Route path="/faq" element={<P.FAQ />} />
          <Route path="/stornierung" element={<P.Cancellation />} />
          <Route path="/rate/:reservationId" element={<P.RateReservation />} />
          <Route path="/group/:token" element={<P.GroupJoin />} />
          <Route path="/swiper" element={<P.DealSwiper />} />
          <Route path="/food-groups" element={<P.FoodGroups />} />
          <Route path="/food-board" element={<P.FoodBoard />} />
          <Route path="/social" element={<P.Social />} />
          <Route path="/social/profile/:userId" element={<P.SocialProfile />} />
          <Route path="/social/post/:postId" element={<P.PostDetail />} />
          <Route path="/explore" element={<P.Explore />} />
          <Route path="/saisonal" element={<P.SeasonalDeals />} />
          <Route path="/vergleich" element={<P.Compare />} />
          <Route path="/favoriten" element={<P.Favorites />} />
          <Route path="/achievements" element={<P.AchievementsPage />} />
          <Route path="/guest-dashboard" element={<P.GuestDashboard />} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<P.DashboardHome />} />
          <Route path="/dashboard/onboarding" element={<P.Onboarding />} />
          <Route path="/dashboard/deals" element={<P.DealsList />} />
          <Route path="/dashboard/deals/new" element={<P.CreateDeal />} />
          <Route path="/dashboard/deals/quick" element={<P.QuickCreateDeal />} />
          <Route path="/dashboard/deals/:id/edit" element={<P.EditDeal />} />
          <Route path="/dashboard/scanner" element={<P.Scanner />} />
          <Route path="/dashboard/stats" element={<P.Stats />} />
          <Route path="/dashboard/settings" element={<P.Settings />} />
          <Route path="/dashboard/loyalty" element={<P.LoyaltyDashboard />} />
          <Route path="/dashboard/revenue" element={<P.RevenueIntelligence />} />
          <Route path="/dashboard/messages" element={<P.DashboardMessages />} />
          <Route path="/dashboard/customers" element={<P.CustomerGeography />} />
          <Route path="/dashboard/reviews" element={<P.ReviewsDashboard />} />
          <Route path="/dashboard/menu" element={<P.MenuManagement />} />
          <Route path="/dashboard/pickup" element={<P.PickupOrders />} />
          <Route path="/dashboard/floorplan" element={<P.FloorPlan />} />
          <Route path="/dashboard/billing" element={<P.BillingView />} />
          <Route path="/dashboard/transparency" element={<P.Transparency />} />
          <Route path="/dashboard/crm" element={<P.GuestCRMPage />} />
          <Route path="/dashboard/marketing" element={<P.MarketingPage />} />
          <Route path="/dashboard/social" element={<P.RestaurantSocial />} />
          <Route path="/dashboard/foodboard" element={<P.FoodBoardDashboard />} />
          <Route path="/dashboard/hub" element={<P.QuickHub />} />
          <Route path="/dashboard/notifications" element={<P.DashboardNotifications />} />
          <Route path="/dashboard/service-types" element={<P.ServiceTypes />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<P.AdminHome />} />
          <Route path="/admin/activity" element={<P.AdminActivity />} />
          <Route path="/admin/restaurants" element={<P.AdminRestaurants />} />
          <Route path="/admin/guests" element={<P.AdminGuests />} />
          <Route path="/admin/restaurants/:restaurantId" element={<P.AdminRestaurantDetail />} />
          <Route path="/admin/guests/:userId" element={<P.AdminGuestDetail />} />
          <Route path="/admin/reservations" element={<P.AdminReservations />} />
          <Route path="/admin/reviews" element={<P.AdminReviews />} />
          <Route path="/admin/billing" element={<P.AdminBilling />} />
          <Route path="/admin/messages" element={<P.AdminMessages />} />
          <Route path="/admin/contact" element={<P.AdminContactMessages />} />
          <Route path="/admin/audit-log" element={<P.AdminAuditLog />} />
          <Route path="/admin/social" element={<P.AdminSocial />} />
          <Route path="/admin/analytics" element={<P.AdminAnalytics />} />
          <Route path="/admin/reports" element={<P.AdminReports />} />
          <Route path="/admin/roles" element={<P.AdminRoles />} />
          <Route path="/admin/system" element={<P.AdminSystemHealth />} />
          <Route path="/admin/seo" element={<P.AdminSeoFindings />} />

          {/* Catch-all */}
          <Route path="/dev/typography" element={<P.TypographyAudit />} />

          {/* Catch-all */}
          <Route path="*" element={<P.NotFound />} />
        </Routes>
        {showFooter && <Footer />}
      </PageTransition>
    </AnimatePresence>
  );
}
